import React, { createContext, useContext, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { fetchGraphQL } from "contexts/Relay";
import { useSession } from "contexts/Session";
import Spinner from "components/Spinner";

import defaultProperties from "./themes/default-properties.scss";
import defaultTheme from "./themes/default.scss";
import energyTheme from "./themes/energy.scss";
import fallTheme from "./themes/fall.scss";
import fashionTheme from "./themes/fashion.scss";
import nightTheme from "./themes/night.scss";
import noirTheme from "./themes/noir.scss";
import springTheme from "./themes/spring.scss";
import summerTheme from "./themes/summer.scss";

const themePresets = {
  default: defaultTheme,
  energy: energyTheme,
  fall: fallTheme,
  fashion: fashionTheme,
  night: nightTheme,
  noir: noirTheme,
  spring: springTheme,
  summer: summerTheme,
} as const;

type ThemePreset = keyof typeof themePresets;

const isThemePreset = (theme: string): theme is ThemePreset => {
  return themePresets.hasOwnProperty(theme);
};

const getThemeUrl = (theme: string | null) => {
  if (!theme) {
    return defaultTheme;
  }
  return isThemePreset(theme) ? themePresets[theme] : theme;
};

const tenantQuery = `
    query Tenant_getTenant_Query {
      tenant {
        authConfig {
          recaptchaSiteKey
        }
        design {
          logo
          theme
        }
        deviceClaimInsteadOfApplianceRegistration
        deviceClaimWithImplicitSerial
        deviceClaimWithTokenChallenge
      }
      latestCookiePolicy {
        id
        text
      }
      latestPrivacyPolicy {
        id
        text
      }
      latestTermsAndConditions {
        id
        text
      }
    }
  `;

type AuthConfig = {
  recaptchaSiteKey: string | null;
};

type Design = {
  theme: string | null;
  logo: string | null;
};

type TenantConfig = {
  authConfig: AuthConfig;
  design: Design;
  deviceClaimInsteadOfApplianceRegistration: boolean;
  deviceClaimWithImplicitSerial: boolean;
  deviceClaimWithTokenChallenge: boolean;
  latestCookiePolicy: {
    id: string;
    text: string;
  };
  latestPrivacyPolicy: {
    id: string;
    text: string;
  };
  latestTermsAndConditions: {
    id: string;
    text: string;
  };
};

const TenantConfigContext = createContext<TenantConfig | null>(null);

type TenantConfigProviderProps = {
  children: React.ReactNode;
};

const TenantConfigProvider = ({ children }: TenantConfigProviderProps) => {
  const { baseBackendUrl, tenantSlug } = useSession();
  const [isFetchingTenantConfig, setIsFetchingTenantConfig] = useState(true);
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);

  useEffect(() => {
    setIsFetchingTenantConfig(true);
    fetchGraphQL(tenantQuery, {}, null, baseBackendUrl, tenantSlug)
      .then((response) => {
        setTenantConfig({
          latestCookiePolicy: response.data.latestCookiePolicy,
          latestPrivacyPolicy: response.data.latestPrivacyPolicy,
          latestTermsAndConditions: response.data.latestTermsAndConditions,
          ...response.data.tenant,
        });
      })
      .catch(() => {
        setTenantConfig(null);
      })
      .finally(() => {
        setIsFetchingTenantConfig(false);
      });
  }, [baseBackendUrl, tenantSlug]);

  if (isFetchingTenantConfig) {
    return (
      <div
        className="vh-100 d-flex justify-content-center align-items-center"
        data-testid="tenant-config-context-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (!tenantConfig) {
    // TODO move the language provider up in the context list
    return (
      <div
        className="vh-100 d-flex justify-content-center align-items-center"
        data-testid="tenant-config-context-error"
      >
        <h3>Could not fetch remote configuration.</h3>
        <p>Please verify that the app is set up correctly.</p>
      </div>
    );
  }

  const themeUrl = getThemeUrl(tenantConfig.design.theme);

  return (
    <HelmetProvider>
      <Helmet>
        <link href={defaultProperties} type="text/css" rel="stylesheet" />
        <link href={themeUrl} type="text/css" rel="stylesheet" />
      </Helmet>
      <TenantConfigContext.Provider value={tenantConfig}>
        {children}
      </TenantConfigContext.Provider>
    </HelmetProvider>
  );
};

const useTenantConfig = (): TenantConfig => {
  const contextValue = useContext(TenantConfigContext);
  if (contextValue == null) {
    throw new Error("TenantConfigContext has not been Provided");
  }
  return contextValue;
};

export { getThemeUrl, TenantConfigContext, useTenantConfig };

export default TenantConfigProvider;
